import { useCallback, useMemo } from "react"

// ** Third Party Components
import { Column } from "@tanstack/react-table"
import { ArrowDownIcon, ArrowUpIcon, CaretSortIcon, EyeNoneIcon } from "@radix-ui/react-icons"

// ** Theme Components
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../common/dropdown-menu"
import { Button } from "../common/button"

// ** Helper
import { cn } from "@pma/utils/helpers"

// ** Custom Hooks
import useTranslator from "@pma/hooks/use-translator"

interface DataTableColumnHeaderProps<TData, TValue> extends React.HTMLAttributes<HTMLDivElement> {
  column: Column<TData, TValue>
  title: string
  pageSorting?: { orderBy: string; sortBy: string }
  pagination?: { pageIndex: number; pageSize: number }
  setPageSorting?: (sortingObj: any) => void
  setPagination?: (pagination: any) => void
  className: string
}

const WorkDiariesColumnHeader = ({
  column,
  title,
  pageSorting,
  pagination,
  setPageSorting,
  setPagination,
  className,
}: DataTableColumnHeaderProps<any, any>) => {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"

  // Derive icon state from pageSorting directly instead of using useState + useEffect
  const iconState = useMemo(() => {
    if (pageSorting?.sortBy === column.id) {
      return { order: pageSorting.orderBy, column: column.id }
    }
    return { order: "false", column: "" }
  }, [pageSorting?.sortBy, pageSorting?.orderBy, column.id])

  const handleAscending = useCallback(() => {
    const payload = { orderBy: "asc", sortBy: column.id }
    if (setPageSorting) {
      setPageSorting(payload)
    }
    if (setPagination && pagination) {
      setPagination({ pageIndex: 1, pageSize: pagination.pageSize })
    }
  }, [column.id, setPageSorting, setPagination, pagination])

  const handleDescending = useCallback(() => {
    const payload = { orderBy: "desc", sortBy: column.id }
    if (setPageSorting) {
      setPageSorting(payload)
    }
    if (setPagination && pagination) {
      setPagination({ pageIndex: 1, pageSize: pagination.pageSize })
    }
  }, [column.id, setPageSorting, setPagination, pagination])

  const handleVisibility = useCallback(() => {
    column.toggleVisibility(false)
  }, [column])

  // Memoize the icon component
  const SortIcon = useMemo(() => {
    if (iconState.column === column.id) {
      if (iconState.order === "asc") {
        return <ArrowUpIcon className='ml-2 h-4 w-4 shrink-0' />
      } else if (iconState.order === "desc") {
        return <ArrowDownIcon className='ml-2 h-4 w-4 shrink-0' />
      }
    }
    return <CaretSortIcon className='ml-2 h-4 w-4 shrink-0' />
  }, [iconState.column, iconState.order, column.id])

  // Memoize translated title to ensure it updates when locale changes
  const translatedTitle = useMemo(() => translate(title), [translate, title, locale])

  if (!column.getCanSort()) {
    return (
      <span
        className={cn(
          "flex w-full min-w-[6ch] items-center text-black",
          className,
          isHebrew ? "rtl text-right" : "ltr text-left",
        )}
      >
        {translatedTitle}
      </span>
    )
  }

  return (
    <div className={cn("flex w-full min-w-0 items-center space-x-2", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='sm'
            className='h-8 truncate px-0 text-black data-[state=open]:bg-accent'
          >
            <span className='min-w-0 flex-1 truncate text-left'>{translatedTitle}</span>
            {SortIcon}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='start'>
          <DropdownMenuItem onClick={handleAscending}>
            <ArrowUpIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            {translate("Asc")}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleDescending}>
            <ArrowDownIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            {translate("Desc")}
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleVisibility}>
            <EyeNoneIcon className='mr-2 h-3.5 w-3.5 text-muted-foreground/70' />
            {translate("Hide")}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export default WorkDiariesColumnHeader
